import React, { useEffect, useMemo, useRef, useState } from "react";
import { LocalInitClientNotificationDTO } from "../../../models/local/local.response";
import { CloseWhiteIcon } from "../../../assets/icons";


interface NotificationsProps {
    notifications?: LocalInitClientNotificationDTO[];
}

type UiNotification = LocalInitClientNotificationDTO & {
    createdAt: number;
    remainingMs: number;
    isPaused: boolean;
    isClosing: boolean; // za exit animaciju
};

const EXIT_ANIMATION_MS = 220;

const Notifications: React.FC<NotificationsProps> = ({ notifications }) => {

    const exitTimeoutsRef = useRef<Record<string, number>>({});
    const [items, setItems] = useState<UiNotification[]>([]);
    const intervalRef = useRef<number | null>(null);

    const autoCloseMs = 10000;

    useEffect(() => {
        if (!notifications)
            return;

        setItems((prev) => {
            const prevIds = new Set(prev.map((x) => x.id));
            const incomingNew = notifications
                .filter((n) => !prevIds.has(n.id))
                .map<UiNotification>((n) => ({
                    ...n,
                    createdAt: Date.now(),
                    remainingMs: autoCloseMs,
                    isPaused: false,
                    isClosing: false,
                }));

            return [...prev, ...incomingNew];
        });
    }, [notifications, autoCloseMs]);

    useEffect(() => {
        if (intervalRef.current != null)
            return;

        intervalRef.current = window.setInterval(() => {

            setItems((prev) => {
                const now = Date.now();
                let changed = false;

                const next = prev.map((n) => {
                    if (n.isClosing || n.isPaused)
                        return n;

                    // tick step (ms)
                    const step = 100;
                    const newRemaining = Math.max(0, n.remainingMs - step);

                    if (newRemaining !== n.remainingMs) changed = true;

                    if (newRemaining === 0 && !n.isClosing) {
                        changed = true;
                        return { ...n, remainingMs: 0, isClosing: true };
                    }

                    return { ...n, remainingMs: newRemaining };
                });

                return changed ? next : prev;
            });

        }, 100);

        return () => {
            if (intervalRef.current != null) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        items.forEach((n) => {
            if (!n.isClosing) return;

            if (exitTimeoutsRef.current[n.id]) return; // već zakazano

            exitTimeoutsRef.current[n.id] = window.setTimeout(() => {
                setItems((prev) => prev.filter((x) => x.id !== n.id));

                window.clearTimeout(exitTimeoutsRef.current[n.id]);
                delete exitTimeoutsRef.current[n.id];
            }, EXIT_ANIMATION_MS);
        });

        return () => { };
    }, [items]);


    const close = (id: string) => {
        setItems((prev) =>
            prev.map((x) => (x.id === id ? { ...x, isClosing: true } : x))
        );
    };

    const pause = (id: string) => {
        setItems((prev) =>
            prev.map((x) => (x.id === id ? { ...x, isPaused: true } : x))
        );
    };

    const resume = (id: string) => {
        setItems((prev) =>
            prev.map((x) => (x.id === id ? { ...x, isPaused: false } : x))
        );
    };

    return (
        <section className="client-notification-section">
            <div className="w-layout-blockcontainer container w-container">
                <div className="client-notification-wrapper">
                    <div className="client-notification-list-div">
                        {items.map((n) => {
                            const progressPct = Math.round(
                                (n.remainingMs / autoCloseMs) * 100
                            );

                            return (
                                <div
                                    key={n.id}
                                    className={[
                                        "client-notification-div",
                                        "notif",
                                        n.isClosing ? "notif-exit" : "notif-enter",
                                        n.isPaused ? "notif-paused" : "",
                                    ].join(" ")}
                                    // "drži prst" / klik i hold (mouse)
                                    onMouseDown={() => pause(n.id)}
                                    onMouseUp={() => resume(n.id)}
                                    onMouseLeave={() => resume(n.id)}
                                    // touch (telefon)
                                    onTouchStart={() => pause(n.id)}
                                    onTouchEnd={() => resume(n.id)}
                                >
                                    <div className="client-notification-header-div">
                                        <div>
                                            <div className="client-notification-title-text">
                                                {n.title}
                                            </div>
                                        </div>

                                        <div
                                            className="client-notification-header-close-div notif-close-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                close(n.id);
                                            }}
                                            aria-label="Close notification"
                                        >
                                            <img
                                                src={CloseWhiteIcon}
                                                loading="lazy"
                                                alt=""
                                                className="client-notification-cose-image"
                                            />
                                        </div>
                                    </div>

                                    <div className="client-notification-text-div">
                                        <div className="client-notification-text" style={{whiteSpace : "pre-wrap"}}>{n.message}</div>
                                    </div>

                                    <div
                                        className={[
                                            "client-notification-progress-div",
                                            "notif-progress",
                                            n.isPaused ? "notif-progress-paused" : "",
                                            n.isClosing ? "notif-progress-paused" : "",
                                        ].join(" ")}
                                        style={
                                            {
                                                ["--duration" as any]: `${autoCloseMs}ms`,
                                            } as React.CSSProperties
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Notifications;
